import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "./ProductScreen.css";
import PurchaseBox from "./PurchaseBox";

import Meta from "../../components/Meta";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import ImageZoom from "../../components/images/ImageZoom";

import {
  getOptionVisualSwatch,
  getProductVisualSwatch,
  getVariantVisualSwatch,
  safeText,
  titleCase,
} from "../../components/swatches/productSwatches";

import {
  getProductByIdentifier,
  resetProductBySlug,
  resetProductDetails,
  resetProductResolve,
} from "../../actions/productActions";

import { addToCart, resetCartError } from "../../actions/cartActions";
import { selectMadeToMeasureDraftProduct } from "../../actions/madeToMeasureActions";
import ProductRecommendations from "./ProductRecommendations";
import { getSafeUserMessage } from "../../utils/errorMessages";
import { SITE_NAME } from "../../constants/brand";
import {
  buildProductSeo,
  buildProductSeoDescription,
  buildProductSeoKeywords,
  buildProductSeoTitle,
} from "../../utils/productSeo";
import {
  getFirstPositivePrice,
  getProductDisplayPriceValue,
} from "../../utils/productPricing";
import {
  MTM_STEPS,
  getMtmEligibilityForStep,
  getMtmReturnStepAfterSelection,
  getMtmSelectionActionLabel,
  getMtmShopForStep,
  normalizeMtmStep,
} from "../../utils/madeToMeasureJourney";

const MAX_QTY_CAP = 20;
const FALLBACK_BRAND = SITE_NAME;
const FALLBACK_IMAGE = "https://placehold.co/900x1200?text=Academy+Costumes+London";

const OPTION_ACTION = Object.freeze({
  NONE: "none",
  ADD_TO_CART: "add_to_cart",
  VIEW_BASKET: "view_basket",
  ADJUST_QUANTITY: "adjust_quantity",
});

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function getErrorText(error) {
  if (!error) return "";
  if (typeof error === "string") {
    return getSafeUserMessage(
      error,
      "We could not load this product right now. Please try again later.",
    );
  }

  return getSafeUserMessage(
    error.customerMessage || error.message || error.error,
    "We could not load this product right now. Please try again later.",
  );
}

function toPositiveInt(value, fallback = 1) {
  const number = Math.floor(Number(value));
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function normalizeChoiceKey(value = "") {
  return safeText(value).toLowerCase().replace(/\s+/g, " ").trim();
}

function normalizeStatus(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeState(value = "") {
  return safeText(value).toUpperCase();
}

function normalizeAction(value = "") {
  return normalizeChoiceKey(value).replace(/\s+/g, "_");
}

function normalizeUpper(value = "") {
  return safeText(value).toUpperCase();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function uniqueTextValues(values = []) {
  const seen = new Set();
  const output = [];

  values.forEach((value) => {
    const text = safeText(value);
    const key = normalizeChoiceKey(text);

    if (!text || !key || seen.has(key)) return;

    seen.add(key);
    output.push(text);
  });

  return output;
}

function getImageUrl(image) {
  if (typeof image === "string") return safeText(image);
  if (!image || typeof image !== "object") return "";

  return safeText(
    image.url ||
      image.src ||
      image.image ||
      image.imageUrl ||
      image.defaultImage ||
      image.defaultImageUrl ||
      image.secure_url ||
      image.secureUrl ||
      image.thumbnail ||
      image.thumbnailUrl ||
      "",
  );
}

function uniqueImages(items = [], productName = "") {
  const seen = new Set();
  const output = [];

  items.forEach((item) => {
    const url = getImageUrl(item);
    if (!url || seen.has(url)) return;

    seen.add(url);

    output.push({
      ...(typeof item === "object" && item ? item : {}),
      url,
      src: url,
      image: url,
      imageUrl: url,
      alt:
        typeof item === "object" && item?.alt
          ? safeText(item.alt)
          : productName || "Product image",
    });
  });

  return output;
}

function buildProductGallery(product = {}) {
  return uniqueImages(
    [
      ...(Array.isArray(product.images) ? product.images : []),
      product.defaultImage,
    ],
    product.name,
  );
}

function buildSelectionGallery(
  selection = null,
  productGallery = [],
  productName = "",
) {
  const selectionName =
    safeText(selection?.name) || safeText(selection?.label) || productName;
  const selectionGallery = uniqueImages(
    Array.isArray(selection?.images) ? selection.images : [],
    selectionName,
  );

  if (selectionGallery.length) return selectionGallery;

  const productImageUrls = new Set(productGallery.map((image) => image.url));
  return uniqueImages(
    [
      selection?.image,
      selection?.imageUrl,
      selection?.defaultImage,
      selection?.defaultImageUrl,
    ],
    selectionName,
  ).filter((image) => !productImageUrls.has(image.url));
}

function buildGallery(product = {}, selectedSelection = null) {
  const productGallery = buildProductGallery(product);
  const selectionGallery = buildSelectionGallery(
    selectedSelection,
    productGallery,
    product.name,
  );

  if (selectionGallery.length) return selectionGallery;

  if (productGallery.length) return productGallery;

  return [
    {
      url: FALLBACK_IMAGE,
      src: FALLBACK_IMAGE,
      alt: product.name || "Product image",
    },
  ];
}

function getRawOptionParams(option) {
  const safeOption = asObject(option);
  const value = asObject(safeOption.value);

  return {
    variantSku: normalizeUpper(safeOption.variantSku || value.variantSku),
    variantType: normalizeLower(safeOption.variantType || value.variantType),
  };
}

function getSelectionParams(product = {}, option = {}) {
  const params = getRawOptionParams(option);

  const productType = normalizeLower(product?.productType);
  const productSku = normalizeUpper(product?.sku);

  if (productType === "textile") {
    return {
      variantSku: "",
      variantType: params.variantType,
    };
  }

  if (
    !product?.isVariant &&
    params.variantSku &&
    productSku &&
    params.variantSku === productSku
  ) {
    return {
      variantSku: "",
      variantType:
        params.variantType || (productType === "standard" ? "standard" : ""),
    };
  }

  return params;
}

function getOptionKey(option) {
  const safeOption = asObject(option);
  const params = getRawOptionParams(safeOption);

  return [
    params.variantSku || "no-sku",
    params.variantType || "standard",
    safeText(safeOption.label) || "option",
  ].join("::");
}

function isOptionDisplayable(option = {}) {
  return option.isDisplayable !== false && option.displayInOptions !== false;
}

function getOptionStatus(option = {}) {
  return normalizeStatus(option.status || option.stockStatus);
}

function getOptionState(option = {}) {
  return normalizeState(
    option.purchaseState || option.unavailableState || option.reason,
  );
}

function getOptionPrimaryAction(option = {}) {
  return normalizeAction(option.primaryAction);
}

function getRemainingAddableQty(option = {}) {
  const value = Number(
    option.remainingAddableQty ?? option.maxAddableQty ?? option.available ?? 0,
  );

  if (!Number.isFinite(value)) return 0;

  return Math.max(0, Math.floor(value));
}

function getAlreadyInCartQty(option = {}) {
  const value = Number(option.alreadyInCartQty ?? 0);

  if (!Number.isFinite(value)) return 0;

  return Math.max(0, Math.floor(value));
}

function isOptionInBasket(option = {}) {
  const status = getOptionStatus(option);
  const state = getOptionState(option);
  const action = getOptionPrimaryAction(option);

  return Boolean(
    option.isInBasket ||
    option.isMaxedInCart ||
    option.maxInCart ||
    getAlreadyInCartQty(option) > 0 ||
    state === "MAX_IN_CART" ||
    status === "max_in_cart" ||
    status === "shared_stock_exhausted_in_cart" ||
    action === OPTION_ACTION.VIEW_BASKET,
  );
}

function isOptionSoldOut(option = {}) {
  const status = getOptionStatus(option);
  const state = getOptionState(option);

  return (
    state === "SOLD_OUT" ||
    status === "out_of_stock" ||
    status === "sold_out" ||
    option.soldOut === true
  );
}

function isOptionHardUnavailable(option = {}) {
  const status = getOptionStatus(option);
  const state = getOptionState(option);

  return (
    state === "UNAVAILABLE" ||
    status === "unavailable" ||
    state === "COMING_SOON" ||
    status === "coming_soon"
  );
}

function isOptionSelectable(option = {}) {
  if (!option || !isOptionDisplayable(option)) return false;

  if (option.canSelect === true || option.isSelectable === true) return true;
  if (isOptionInBasket(option)) return true;

  if (option.canSelect === false || option.isSelectable === false) return false;
  if (isOptionHardUnavailable(option)) return false;

  const status = getOptionStatus(option);
  const state = getOptionState(option);

  if (
    state === "RESERVED" ||
    state === "RESERVED_BY_ANOTHER_CUSTOMER" ||
    status === "reserved"
  ) {
    return false;
  }

  if (isOptionSoldOut(option)) return false;

  if (
    status === "requested_qty_exceeds_available" ||
    status === "insufficient_stock" ||
    state === "INSUFFICIENT_STOCK"
  ) {
    return true;
  }

  if (option.disabled === true) return false;

  return (
    option.isAddable === true ||
    option.canAddToCart === true ||
    option.isAvailable === true ||
    getRemainingAddableQty(option) > 0
  );
}

function isOptionAddable(option = {}) {
  if (!option || !isOptionDisplayable(option)) return false;
  if (!isOptionSelectable(option)) return false;

  const status = getOptionStatus(option);
  const state = getOptionState(option);

  if (isOptionInBasket(option)) return false;
  if (isOptionSoldOut(option)) return false;
  if (isOptionHardUnavailable(option)) return false;

  if (
    state === "RESERVED" ||
    state === "RESERVED_BY_ANOTHER_CUSTOMER" ||
    status === "reserved" ||
    state === "INSUFFICIENT_STOCK" ||
    status === "requested_qty_exceeds_available" ||
    status === "insufficient_stock" ||
    status === "shared_stock_blocked_by_cart" ||
    status === "shared_stock_exhausted_in_cart"
  ) {
    return false;
  }

  if (option.disabled === true) return false;
  if (option.isAddable === false) return false;
  if (option.canAddToCart === false) return false;
  if (option.isAvailable === false) return false;

  if (
    option.isAddable === true ||
    option.canAddToCart === true ||
    option.isAvailable === true
  ) {
    return getRemainingAddableQty(option) > 0;
  }

  const available = Number(option.available);
  if (Number.isFinite(available)) return available > 0;

  return getRemainingAddableQty(option) > 0;
}

function hasExplicitAddableFlag(option) {
  return (
    option?.isAddable === true ||
    option?.canAddToCart === true ||
    option?.isAvailable === true
  );
}

function optionMatchesQuery(option = {}, searchParams) {
  const params = getRawOptionParams(option);
  const querySku = normalizeUpper(searchParams.get("variantSku"));
  const queryType = normalizeLower(searchParams.get("variantType"));

  if (!querySku && !queryType) return false;
  if (querySku && params.variantSku !== querySku) return false;
  if (queryType && params.variantType !== queryType) return false;

  return true;
}

function optionMatchesVariant(option = {}, variant = {}) {
  const params = getRawOptionParams(option);
  const variantSku = normalizeUpper(variant.variantSku || variant.sku);
  const variantType = normalizeLower(variant.variantType);

  if (variantSku || params.variantSku) {
    return Boolean(
      variantSku && params.variantSku && variantSku === params.variantSku,
    );
  }

  return Boolean(
    variantType && params.variantType && variantType === params.variantType,
  );
}

function getVariantForOption(product = {}, option = null) {
  if (!option) return null;

  const variants = Array.isArray(product.variants) ? product.variants : [];
  return (
    variants.find((variant) => optionMatchesVariant(option, variant)) || null
  );
}

function hasColourData(item = {}) {
  const singleValues = [
    item.color,
    item.colour,
    item.colorName,
    item.colourName,
    item.colorwayLabel,
    item.colourwayLabel,
  ];

  const arrayValues = [
    item.colors,
    item.colours,
    item.colorway,
    item.colourway,
    item.palette,
  ];

  return (
    singleValues.some((value) => safeText(value)) ||
    arrayValues.some((value) => Array.isArray(value) && value.length > 0)
  );
}

function getOptionColourLabel(option) {
  const safeOption = asObject(option);

  return safeText(
    safeOption.color ||
      safeOption.colour ||
      safeOption.colorName ||
      safeOption.colourName ||
      safeOption.colorwayLabel ||
      safeOption.colourwayLabel,
  );
}

function getOptionSizeLabel(option) {
  const safeOption = asObject(option);
  return safeText(safeOption.size);
}

function getOptionColourKey(option) {
  return normalizeChoiceKey(getOptionColourLabel(option));
}

function getOptionSizeKey(option) {
  return normalizeChoiceKey(getOptionSizeLabel(option));
}

function getSwatchSignature(swatch = {}) {
  if (swatch.kind === "image") return `image:${swatch.value}`;

  return `${swatch.kind}:${(swatch.rawValues || swatch.values || [])
    .map((value) => safeText(value).toLowerCase())
    .join("|")}`;
}

function addChoiceItem(map, { key, label, swatch, option, image }) {
  const safeKey = normalizeChoiceKey(key || label);
  if (!safeKey) return;

  const optionKey = option ? getOptionKey(option) : "";
  const existing = map.get(safeKey);

  const optionAddable = option ? isOptionAddable(option) : true;
  const optionSelectable = option ? isOptionSelectable(option) : true;
  const optionBasketAction = option ? isOptionInBasket(option) : false;

  if (existing) {
    if (optionKey && !existing.optionKeys.includes(optionKey)) {
      existing.optionKeys.push(optionKey);
    }

    if (optionAddable) existing.isAddable = true;
    if (optionSelectable) existing.isSelectable = true;
    if (optionBasketAction) existing.hasBasketAction = true;

    return;
  }

  map.set(safeKey, {
    key: safeKey,
    label: titleCase(label || key),
    ariaLabel: `Select ${titleCase(label || key)}`,
    swatch,
    optionKeys: optionKey ? [optionKey] : [],
    image,
    isAddable: optionAddable,
    isSelectable: optionSelectable,
    hasBasketAction: optionBasketAction,
  });
}

function buildColourItems(product = {}, options = []) {
  const optionMap = new Map();
  const useVariantSwatches = Boolean(
    product?.isVariant &&
      options.some(
        (option) =>
          getOptionColourLabel(option) || safeText(option?.swatchImage),
      ),
  );

  options.filter(isOptionDisplayable).forEach((option) => {
    const colourLabel = getOptionColourLabel(option);
    const label =
      colourLabel ||
      (useVariantSwatches ? safeText(option.label) : "");
    if (!label) return;

    addChoiceItem(optionMap, {
      key:
        safeText(option.swatchImage) ||
        safeText(option.variantSku) ||
        colourLabel ||
        label,
      label: safeText(option.label) || label,
      swatch: getOptionVisualSwatch(option),
      option,
      image: option.image,
    });
  });

  const optionItems = Array.from(optionMap.values());

  if (optionItems.length) return optionItems;

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variantMap = new Map();

  variants.forEach((variant, index) => {
    if (!hasColourData(variant)) return;

    const swatch = getVariantVisualSwatch(variant);
    if (!swatch?.colourCount && swatch?.kind !== "image") return;

    const matchingOption = options.find((option) =>
      optionMatchesVariant(option, variant),
    );

    const label =
      safeText(variant.color) ||
      safeText(variant.colour) ||
      safeText(variant.name) ||
      swatch.label ||
      `Colour ${index + 1}`;

    addChoiceItem(variantMap, {
      key: getSwatchSignature(swatch) || label,
      label,
      swatch,
      option: matchingOption,
      image:
        variant.defaultImage ||
        getImageUrl(Array.isArray(variant.images) ? variant.images[0] : ""),
    });
  });

  const variantItems = Array.from(variantMap.values());

  if (variantItems.length) return variantItems;

  const productSwatch = getProductVisualSwatch(product);

  if (productSwatch?.colourCount || productSwatch?.kind === "image") {
    return [
      {
        key: "product-colourway",
        label: productSwatch.label || "Colourway",
        ariaLabel: productSwatch.label || "Product colourway",
        swatch: productSwatch,
        optionKeys: [],
        image: product.defaultImage,
        isAddable: true,
        isSelectable: true,
        hasBasketAction: false,
      },
    ];
  }

  return [];
}

function buildSizeItems(options = []) {
  const map = new Map();

  options.filter(isOptionDisplayable).forEach((option) => {
    const label =
      getOptionSizeLabel(option) || option.label || option.unitLabel;

    if (!label) return;

    addChoiceItem(map, {
      key: label,
      label,
      option,
      image: option.image,
    });
  });

  return Array.from(map.values());
}

function getOptionsForChoice(item = {}, options = []) {
  const keys = Array.isArray(item.optionKeys) ? item.optionKeys : [];

  return keys
    .map((key) => options.find((option) => getOptionKey(option) === key))
    .filter(Boolean);
}

function pickBestOption(candidates = [], selectedOption = null) {
  if (!candidates.length) return null;

  const displayableCandidates = candidates.filter(isOptionDisplayable);
  const selectableCandidates = displayableCandidates.filter(isOptionSelectable);
  const addableCandidates = selectableCandidates.filter(isOptionAddable);

  const pool =
    addableCandidates.length ||
    selectableCandidates.length ||
    displayableCandidates.length
      ? addableCandidates.length
        ? addableCandidates
        : selectableCandidates.length
          ? selectableCandidates
          : displayableCandidates
      : candidates;

  const selectedColourKey = getOptionColourKey(selectedOption);
  const selectedSizeKey = getOptionSizeKey(selectedOption);
  const selectedVariantType = getRawOptionParams(selectedOption).variantType;

  return (
    pool.find(
      (option) =>
        selectedColourKey &&
        selectedSizeKey &&
        getOptionColourKey(option) === selectedColourKey &&
        getOptionSizeKey(option) === selectedSizeKey,
    ) ||
    pool.find(
      (option) =>
        selectedSizeKey && getOptionSizeKey(option) === selectedSizeKey,
    ) ||
    pool.find(
      (option) =>
        selectedColourKey && getOptionColourKey(option) === selectedColourKey,
    ) ||
    pool.find(
      (option) =>
        selectedVariantType &&
        getRawOptionParams(option).variantType === selectedVariantType,
    ) ||
    pool[0] ||
    null
  );
}

function getProductFromState(productState = {}) {
  return (
    productState.product ||
    productState.item ||
    productState.data ||
    productState.productDetails ||
    null
  );
}

function getDescriptionParagraphs(description = "") {
  return safeText(description)
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatProductType(value = "") {
  const type = safeText(value);
  if (!type) return "";
  if (type === "readyMade") return "Ready-made";
  return titleCase(type);
}

function getPurchaseOptionLabel(product = {}) {
  const type = normalizeLower(product.productType);

  if (type === "textile") return "Purchase option";
  if (type === "haberdashery") return "Selection";

  return "Size";
}

function buildSpecificationRows(product = {}, selectedOption = null) {
  const readyMade = product.readyMade || {};
  const textile = product.textile || {};
  const haberdashery = product.haberdashery || {};

  const selectedColours = uniqueTextValues([
    ...(Array.isArray(selectedOption?.colors) ? selectedOption.colors : []),
    selectedOption?.color || selectedOption?.colour,
  ]);
  const colours = selectedColours.length
    ? selectedColours
    : uniqueTextValues([
        ...(Array.isArray(product.colors) ? product.colors : []),
        product.color,
      ]);

  const selectedMaterials = uniqueTextValues([
    ...(Array.isArray(selectedOption?.materials)
      ? selectedOption.materials
      : []),
    selectedOption?.material,
  ]);
  const materials = selectedMaterials.length
    ? selectedMaterials
    : uniqueTextValues([
        ...(Array.isArray(product.materials) ? product.materials : []),
        product.material,
      ]);

  const selectedSizes = uniqueTextValues([
    ...(Array.isArray(selectedOption?.sizes) ? selectedOption.sizes : []),
    selectedOption?.size,
  ]);
  const sizes = selectedSizes.length
    ? selectedSizes
    : uniqueTextValues([
        ...(Array.isArray(product.sizes) ? product.sizes : []),
        product.size,
      ]);

  const rows = [
    ["Product type", formatProductType(product.productType)],
    ["Colour", colours.join(", ")],
    ["Material", materials.join(", ")],
    [
      "Pattern",
      Array.isArray(product.patterns)
        ? uniqueTextValues(product.patterns).join(", ")
        : "",
    ],
    ["Size", sizes.join(", ")],
    ["Origin", product.origin],
    ["Use", product.usage],
    ["Care", product.careInstructions],
    ["Finish", haberdashery.finish],
    [
      "Width",
      haberdashery.widthMm !== null && haberdashery.widthMm !== undefined
        ? `${haberdashery.widthMm} mm`
        : "",
    ],
    [
      "Diameter",
      haberdashery.diameterMm !== null && haberdashery.diameterMm !== undefined
        ? `${haberdashery.diameterMm} mm`
        : "",
    ],
    ["Compatibility", haberdashery.compatibility],
    ["Era", readyMade.era],
    ["Fit", readyMade.fit],
    ["Silhouette", readyMade.silhouette],
    ["Closure", readyMade.closure],
    [
      "Unit",
      selectedOption?.unitLabel || textile.unitType || haberdashery.unitType,
    ],
    ["SKU", selectedOption?.sku || product.sku],
  ];

  return rows
    .map(([label, value]) => [label, safeText(value)])
    .filter(([, value]) => value);
}

function cleanBackendStockMessage(message = "") {
  const text = safeText(message);
  const normalized = text.toLowerCase();

  if (!text) return "";

  if (
    normalized.includes("already have all available stock") ||
    normalized.includes("maximum quantity already") ||
    normalized.includes("max in basket") ||
    normalized.includes("max in cart")
  ) {
    return "This item is already in your bag.";
  }

  if (
    normalized.includes("temporarily reserved") ||
    normalized.includes("reserved by another customer") ||
    normalized === "reserved"
  ) {
    return "Currently unavailable.";
  }

  if (
    normalized.includes("shared roll stock is already allocated") ||
    normalized.includes("blocked by another cut")
  ) {
    return "Choose another cut or view your basket.";
  }

  if (normalized.includes("can be held")) {
    return text.replace(/can be held/gi, "is available");
  }

  if (normalized === "out of stock") return "Sold out";

  return text;
}

function getFriendlyOptionLabel(option = {}) {
  const status = getOptionStatus(option);
  const state = getOptionState(option);

  const backendLabel = cleanBackendStockMessage(
    option.statusLabel ||
      option.availableLabel ||
      option.purchaseMessage ||
      option.statusReason ||
      option.stockStatusReason ||
      option.disabledReason ||
      option.unavailableReason ||
      "",
  );

  if (isOptionInBasket(option)) return "In your bag";

  if (
    state === "SHARED_STOCK_BLOCKED_BY_CART" ||
    status === "shared_stock_blocked_by_cart"
  ) {
    return "Choose another cut";
  }

  if (
    state === "RESERVED" ||
    state === "RESERVED_BY_ANOTHER_CUSTOMER" ||
    status === "reserved"
  ) {
    return "Currently unavailable";
  }

  if (isOptionSoldOut(option)) return "Sold out";

  if (
    state === "INSUFFICIENT_STOCK" ||
    status === "requested_qty_exceeds_available" ||
    status === "insufficient_stock"
  ) {
    return "Limited stock";
  }

  if (backendLabel) {
    const normalized = backendLabel.toLowerCase();

    if (normalized === "max in basket" || normalized === "max in cart") {
      return "In your bag";
    }

    if (normalized === "out of stock") return "Sold out";
    if (normalized === "allocated in basket") return "In your bag";
    if (normalized === "blocked by another cut") return "Choose another cut";

    return backendLabel;
  }

  const remaining = getRemainingAddableQty(option);

  if (isOptionAddable(option)) {
    if (remaining === 1) return "Only 1 available";
    if (remaining > 1 && remaining <= 3) return `Only ${remaining} available`;
    return "In stock";
  }

  return "Unavailable";
}

function getAvailabilityText({
  selectedOption,
  hasStock,
  maxQty,
  available,
}) {
  if (selectedOption) {
    const label = getFriendlyOptionLabel(selectedOption);
    if (label) return label;
  }

  if (!hasStock) return "Unavailable";

  const safeAvailable = Math.floor(Number(available || maxQty || 0));

  if (safeAvailable === 1) return "Only 1 available";
  if (safeAvailable > 1 && safeAvailable <= 3) {
    return `Only ${safeAvailable} available`;
  }

  return "In stock";
}

function mergeVariantIntoSelectedOption(selectedOption, selectedVariant) {
  if (!selectedOption) return null;
  if (!selectedVariant) return selectedOption;

  const variantImages = Array.isArray(selectedVariant.images)
    ? selectedVariant.images
    : [];
  const optionImages = Array.isArray(selectedOption.images)
    ? selectedOption.images
    : [];
  const images = variantImages.length ? variantImages : optionImages;

  return {
    ...selectedOption,
    ...selectedVariant,
    images,

    label: safeText(selectedVariant.name) || selectedOption.label,
    variantName: safeText(selectedVariant.name),
    value: selectedOption.value,

    image: getImageUrl(images[0]) || selectedOption.image,
    shortDescription: safeText(selectedVariant.shortDescription),
    price: Number(selectedVariant.price ?? selectedOption.price ?? 0),
  };
}

function buildSeoProduct({
  product = {},
  selectedOption = null,
  selectedPrice = 0,
  gallery = [],
  hasStock = false,
  url = "",
  shopKey = "",
}) {
  return buildProductSeo({
    product,
    selectedOption,
    selectedPrice,
    gallery,
    hasStock,
    url,
    shopKey,
  });
}

function getCartFeedbackFromError(error) {
  const code = normalizeUpper(error?.code || error?.statusCode || "");
  const message = cleanBackendStockMessage(
    error?.customerMessage || error?.message || "",
  );

  if (
    code === "MAX_IN_CART" ||
    message.toLowerCase().includes("already in your bag") ||
    message.toLowerCase().includes("already have all available stock")
  ) {
    return {
      tone: "success",
      message: "This item is already in your bag.",
    };
  }

  return {
    tone: "danger",
    message:
      message ||
      "We could not add this product to your basket right now. Please try again later.",
  };
}

export default function ProductScreen() {
  const { shopKey = "", identifier = "" } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();

  const productBySlugState = useSelector((state) => state.productBySlug || {});
  const productDetailsState = useSelector(
    (state) => state.productDetails || {},
  );
  const userAuth = useSelector((state) => state.userAuth || {});

  const product =
    getProductFromState(productBySlugState) ||
    getProductFromState(productDetailsState);

  const loading = Boolean(
    productBySlugState.loading || productDetailsState.loading,
  );

  const error = product
    ? ""
    : getErrorText(productBySlugState.error || productDetailsState.error);

  const productId = safeText(product?._id || product?.id);
  const userId = safeText(userAuth.userInfo?.id || userAuth.userInfo?._id);
  const isAuthenticated = Boolean(
    userAuth.checked && userAuth.isAuthenticated && userId,
  );
  const mtmStep = normalizeMtmStep(searchParams.get("mtmStep"));
  const isMadeToMeasureMode = Boolean(
    mtmStep && getMtmShopForStep(mtmStep) === shopKey,
  );

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectingForMtm, setSelectingForMtm] = useState(false);
  const [madeToMeasureFeedback, setMadeToMeasureFeedback] = useState("");
  const [basketFeedback, setBasketFeedback] = useState({
    tone: "",
    message: "",
  });

  const options = useMemo(
    () =>
      Array.isArray(product?.purchasableOptions)
        ? product.purchasableOptions.filter(Boolean)
        : [],
    [product],
  );

  const displayableOptions = useMemo(
    () => options.filter(isOptionDisplayable),
    [options],
  );

  const selectedOption = useMemo(() => {
    if (!displayableOptions.length) return null;

    return (
      (selectedVariant
        ? displayableOptions.find((option) =>
            optionMatchesVariant(option, selectedVariant),
          )
        : null) ||
      displayableOptions.find(isOptionAddable) ||
      displayableOptions.find(isOptionSelectable) ||
      displayableOptions[0]
    );
  }, [displayableOptions, selectedVariant]);

  const activeSelectedVariant = useMemo(
    () =>
      selectedVariant || getVariantForOption(product || {}, selectedOption),
    [product, selectedOption, selectedVariant],
  );

  const selectedOptionWithVariant = useMemo(
    () =>
      mergeVariantIntoSelectedOption(selectedOption, activeSelectedVariant),
    [activeSelectedVariant, selectedOption],
  );

  const displayProductName =
    safeText(activeSelectedVariant?.name) || safeText(product?.name);

  const selectedOptionKey = useMemo(
    () => (selectedOption ? getOptionKey(selectedOption) : ""),
    [selectedOption],
  );

  const gallery = useMemo(
    () => buildGallery(product || {}, selectedOptionWithVariant),
    [product, selectedOptionWithVariant],
  );

  const selectedOptionWithVariantAddable = useMemo(
    () => isOptionAddable(selectedOptionWithVariant),
    [selectedOptionWithVariant],
  );

  const colourItems = useMemo(
    () => buildColourItems(product || {}, displayableOptions),
    [product, displayableOptions],
  );

  const sizeItems = useMemo(
    () => buildSizeItems(displayableOptions),
    [displayableOptions],
  );

  const selectedPrice =
    getFirstPositivePrice(
      selectedOptionWithVariant?.price,
      selectedOption?.price,
      getProductDisplayPriceValue(product),
      product?.priceFrom,
      product?.price,
    ) ?? 0;

  const available = Number(
    selectedOptionWithVariant?.available ??
      selectedOption?.available ??
      product?.available ??
      0,
  );

  const rawMaxQty = Number(
    activeSelectedVariant?.maxAddableQty ??
      activeSelectedVariant?.remainingAddableQty ??
      activeSelectedVariant?.available ??
      selectedOptionWithVariant?.maxAddableQty ??
      selectedOptionWithVariant?.remainingAddableQty ??
      selectedOption?.maxAddableQty ??
      selectedOption?.remainingAddableQty ??
      available ??
      0,
  );

  const maxQty = selectedOptionWithVariantAddable
    ? Math.max(
        0,
        Math.min(
          Math.floor(Number.isFinite(rawMaxQty) ? rawMaxQty : 0),
          MAX_QTY_CAP,
        ),
      )
    : 0;

  const hasStock = selectedOptionWithVariantAddable && maxQty > 0;

  const selectedCanPurchase =
    selectedOptionWithVariantAddable ||
    hasExplicitAddableFlag(selectedOptionWithVariant);

  const canPurchase =
    Boolean(productId) &&
    Boolean(selectedOptionWithVariant) &&
    selectedOptionWithVariantAddable &&
    selectedCanPurchase &&
    hasStock &&
    qty >= 1 &&
    qty <= maxQty &&
    !addingToCart;

  const availabilityText = useMemo(
    () =>
      getAvailabilityText({
        selectedOption: selectedOptionWithVariant,
        hasStock,
        maxQty,
        available,
      }),
    [available, hasStock, maxQty, selectedOptionWithVariant],
  );

  const productEligibleForMtm = Boolean(
    product && getMtmEligibilityForStep(product, mtmStep),
  );
  const madeToMeasureRequiresStock = Boolean(
    mtmStep && mtmStep !== MTM_STEPS.GARMENT,
  );
  const madeToMeasureAvailabilityFeedback =
    madeToMeasureRequiresStock && !canPurchase
      ? availabilityText || "Out of stock"
      : "";

  const madeToMeasureCanSelect = Boolean(
    isMadeToMeasureMode &&
      productId &&
      productEligibleForMtm &&
      (!madeToMeasureRequiresStock || canPurchase) &&
      !selectingForMtm,
  );

  const madeToMeasureStepLabel = getMtmSelectionActionLabel(mtmStep);

  const selectedColourLabel = useMemo(() => {
    const selectedColourKey = getOptionColourKey(selectedOptionWithVariant);

    const selectedColour = colourItems.find((item) => {
      if (selectedColourKey && item.key === selectedColourKey) return true;
      return item.optionKeys?.includes(selectedOptionKey);
    });

    return selectedColour?.label || colourItems[0]?.label || "";
  }, [colourItems, selectedOptionKey, selectedOptionWithVariant]);

  const selectedSizeLabel = useMemo(() => {
    const selectedSizeKey = getOptionSizeKey(selectedOptionWithVariant);

    const selectedSize = sizeItems.find((item) => {
      if (selectedSizeKey && item.key === selectedSizeKey) return true;
      return item.optionKeys?.includes(selectedOptionKey);
    });

    return selectedSize?.label || "";
  }, [selectedOptionKey, selectedOptionWithVariant, sizeItems]);

  const descriptionParagraphs = useMemo(
    () => getDescriptionParagraphs(product?.description),
    [product?.description],
  );

  const specificationRows = useMemo(
    () => buildSpecificationRows(product || {}, selectedOptionWithVariant),
    [product, selectedOptionWithVariant],
  );

  const optionLabel = useMemo(
    () => getPurchaseOptionLabel(product || {}),
    [product],
  );

  const canonicalPath = `/shop/${shopKey}/product/${identifier}`;

  const seoTitle = useMemo(
    () =>
      buildProductSeoTitle({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        shopKey,
      }),
    [product, selectedOptionWithVariant, shopKey],
  );

  const seoDescription = useMemo(
    () =>
      buildProductSeoDescription({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        shopKey,
      }),
    [product, selectedOptionWithVariant, shopKey],
  );

  const seoKeywords = useMemo(
    () =>
      buildProductSeoKeywords({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        shopKey,
      }),
    [product, selectedOptionWithVariant, shopKey],
  );

  const seoProduct = useMemo(
    () =>
      buildSeoProduct({
        product: product || {},
        selectedOption: selectedOptionWithVariant,
        selectedPrice,
        gallery,
        hasStock,
        url: canonicalPath,
        shopKey,
      }),
    [
      canonicalPath,
      gallery,
      hasStock,
      product,
      selectedOptionWithVariant,
      selectedPrice,
      shopKey,
    ],
  );

  useEffect(() => {
    if (!shopKey || !identifier) return undefined;

    dispatch(getProductByIdentifier(shopKey, identifier)).catch(
      () => undefined,
    );

    return () => {
      dispatch(resetProductBySlug());
      dispatch(resetProductDetails());
      dispatch(resetProductResolve());
      dispatch(resetCartError());
    };
  }, [dispatch, shopKey, identifier]);

  useEffect(() => {
    if (!displayableOptions.length) return;

    const queryOption = displayableOptions.find(
      (option) =>
        optionMatchesQuery(option, searchParams) && isOptionSelectable(option),
    );

    const defaultOption =
      queryOption ||
      displayableOptions.find((option) => {
        const defaultValue = product?.defaultPurchasableOption || {};
        const optionParams = getSelectionParams(product || {}, option);
        const defaultSku = normalizeUpper(defaultValue.variantSku);
        const defaultType = normalizeLower(defaultValue.variantType);

        return (
          optionParams.variantSku === defaultSku &&
          optionParams.variantType === defaultType &&
          isOptionAddable(option)
        );
      }) ||
      displayableOptions.find(isOptionAddable) ||
      displayableOptions.find(isOptionSelectable) ||
      displayableOptions[0];

    const nextVariant = getVariantForOption(product || {}, defaultOption);
    setSelectedVariant((current) =>
      current === nextVariant ? current : nextVariant,
    );
  }, [
    displayableOptions,
    product,
    product?.defaultPurchasableOption,
    searchParams,
    searchParamsKey,
  ]);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [gallery]);

  useEffect(() => {
    if (maxQty <= 0) {
      if (qty !== 1) setQty(1);
      return;
    }

    if (qty <= maxQty) return;

    setQty(maxQty);
  }, [maxQty, qty]);

  const updateUrlSelection = useCallback(
    (option, nextQty = qty) => {
      const params = getSelectionParams(product || {}, option);
      const next = new URLSearchParams(searchParams);

      if (params.variantSku) next.set("variantSku", params.variantSku);
      else next.delete("variantSku");

      if (params.variantType) next.set("variantType", params.variantType);
      else next.delete("variantType");

      if (nextQty > 1) next.set("qty", String(nextQty));
      else next.delete("qty");

      setSearchParams(next, { replace: true });
    },
    [product, qty, searchParams, setSearchParams],
  );

  const selectOption = useCallback(
    (option) => {
      if (!option || !isOptionSelectable(option)) return;

      setBasketFeedback({ tone: "", message: "" });
      setSelectedVariant(getVariantForOption(product || {}, option));
      setQty(1);
      updateUrlSelection(option, 1);
    },
    [product, updateUrlSelection],
  );

  const handleChoiceSelect = useCallback(
    (item) => {
      if (!item || item.isSelectable === false) return;

      const candidates = getOptionsForChoice(item, displayableOptions);

      const nextOption = pickBestOption(candidates, selectedOptionWithVariant);

      if (nextOption && isOptionSelectable(nextOption)) {
        selectOption(nextOption);
      }
    },
    [displayableOptions, selectOption, selectedOptionWithVariant],
  );

  const handleQtyChange = useCallback(
    (nextQty) => {
      const safeQty = Math.min(
        Math.max(toPositiveInt(nextQty, 1), 1),
        maxQty || 1,
      );

      setBasketFeedback({ tone: "", message: "" });
      setQty(safeQty);

      if (selectedOptionWithVariant) {
        updateUrlSelection(selectedOptionWithVariant, safeQty);
      }
    },
    [maxQty, selectedOptionWithVariant, updateUrlSelection],
  );

  const handleAddToBasket = useCallback(async () => {
    if (!canPurchase || !productId || !product || !selectedOptionWithVariant) {
      return;
    }

    const selection = getSelectionParams(product, selectedOptionWithVariant);

    setAddingToCart(true);
    setBasketFeedback({ tone: "", message: "" });
    dispatch(resetCartError());

    try {
      const result = await dispatch(
        addToCart({
          productId,
          qty,
          variantSku: selection.variantSku || "",
          variantType: selection.variantType || "",
        }),
      );

      setBasketFeedback({
        tone: "success",
        message:
          cleanBackendStockMessage(
            result?.customerMessage || result?.message,
          ) || "Added to basket.",
      });
    } catch (cartError) {
      setBasketFeedback(getCartFeedbackFromError(cartError));
    } finally {
      setAddingToCart(false);
    }
  }, [
    canPurchase,
    dispatch,
    product,
    productId,
    qty,
    selectedOptionWithVariant,
  ]);

  const handleSelectForMadeToMeasure = useCallback(async () => {
    if (!isMadeToMeasureMode) return;

    if (!isAuthenticated) {
      const redirect = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }

    if (!productEligibleForMtm) {
      setMadeToMeasureFeedback(
        "This product is not currently available for made to measure.",
      );
      return;
    }

    if (madeToMeasureRequiresStock && !canPurchase) {
      setMadeToMeasureFeedback(
        madeToMeasureAvailabilityFeedback || "Out of stock",
      );
      return;
    }

    setSelectingForMtm(true);
    setMadeToMeasureFeedback("");

    try {
      const result = await dispatch(
        selectMadeToMeasureDraftProduct(mtmStep, {
          productId,
          quantity: qty,
          selectedOption: selectedOptionWithVariant,
        }),
      );

      const nextStep = getMtmReturnStepAfterSelection(mtmStep);

      navigate(`/made-to-measure?step=${encodeURIComponent(nextStep)}`, {
        state: {
          message:
            result?.message ||
            "Selection saved to your made-to-measure draft.",
        },
      });
    } catch (error) {
      setMadeToMeasureFeedback(
        getSafeUserMessage(
          error?.message,
          "We could not save this made-to-measure selection. Please try again later.",
        ),
      );
    } finally {
      setSelectingForMtm(false);
    }
  }, [
    canPurchase,
    dispatch,
    isAuthenticated,
    isMadeToMeasureMode,
    madeToMeasureAvailabilityFeedback,
    madeToMeasureRequiresStock,
    mtmStep,
    navigate,
    productEligibleForMtm,
    productId,
    qty,
    selectedOptionWithVariant,
  ]);

  if (loading) {
    return (
      <main className="product-screen product-screen--state">
        <Loader />
      </main>
    );
  }

  if (error) {
    return (
      <main className="product-screen product-screen--state">
        <Message variant="danger">{error}</Message>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="product-screen product-screen--state">
        <Message variant="info">Product not found.</Message>
      </main>
    );
  }

  return (
    <main className="product-screen">
      <Meta
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={gallery[0]?.url || product.defaultImage}
        url={canonicalPath}
        product={seoProduct}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: titleCase(shopKey), url: `/shop/${shopKey}` },
          {
            name: displayProductName,
            url: canonicalPath,
          },
        ]}
      />

      <div className="product-screen__inner">
        <section className="product-screen__top">
          <div className="product-screen__gallery">
            <ImageZoom
              images={gallery}
              productName={displayProductName}
              selectedImageIndex={selectedImageIndex}
              onImageChange={setSelectedImageIndex}
            />
          </div>

          <PurchaseBox
            product={{ ...product, name: displayProductName }}
            brand={FALLBACK_BRAND}
            selectedPrice={selectedPrice}
            selectedOption={selectedOptionWithVariant}
            selectedOptionKey={selectedOptionKey}
            optionLabel={optionLabel}
            colourItems={colourItems}
            sizeItems={sizeItems}
            options={displayableOptions}
            selectedColourLabel={selectedColourLabel}
            selectedSizeLabel={selectedSizeLabel}
            hasStock={hasStock}
            availabilityText={availabilityText}
            qty={qty}
            maxQty={maxQty}
            resolveLoading={false}
            resolveError=""
            addingToCart={addingToCart}
            basketFeedback={basketFeedback}
            canPurchase={canPurchase}
            madeToMeasureMode={isMadeToMeasureMode}
            madeToMeasureStepLabel={madeToMeasureStepLabel}
            madeToMeasureCanSelect={madeToMeasureCanSelect}
            madeToMeasureLoading={selectingForMtm}
            madeToMeasureFeedback={
              madeToMeasureFeedback ||
              madeToMeasureAvailabilityFeedback ||
              (isMadeToMeasureMode && !isAuthenticated
                ? "Sign in to save this selection to your made-to-measure draft."
                : "")
            }
            descriptionParagraphs={descriptionParagraphs}
            specificationRows={specificationRows}
            onChoiceSelect={handleChoiceSelect}
            onOptionSelect={selectOption}
            onQtyChange={handleQtyChange}
            onAddToBasket={handleAddToBasket}
            onSelectForMadeToMeasure={handleSelectForMadeToMeasure}
          />
        </section>

        <div className="product-screen__recommendations">
          <ProductRecommendations productId={productId} limit={8} />
        </div>
      </div>
    </main>
  );
}
